import React, { useEffect, useState } from 'react';
import {
  CreatePatientReview,
  PatientReview,
  PatientReviewScore,
  ReviewStatus,
} from '../../../../data/PatientReviewData';
import { Alert, Box, CircularProgress, IconButton, Snackbar, Stack, Tooltip } from '@mui/material';
import { AddTaskOutlined } from '@mui/icons-material';
import ReviewNotes from './reviewNotes/ReviewNotes';
import ScorePicker from './scorePicker/ScorePicker';
import DecisionButtons, { DecisionAvailability } from './decisionButtons/DecisionButtons';
import { useApplyClientSideReview, usePatient, usePatientData, useResearchProjectId } from '../../stores/dataStore';
import useAuth from '../../../../auth/UseAuth';
import { initialLoadableData, LoadableData, trackLoading } from '../../utils/loadingUtils';
import CopyButton from '../common/CopyButton';

const ReviewGroup: React.FC<{
  onDoneReviewing: () => void;
}> = ({ onDoneReviewing }) => {
  const researchProjectId = useResearchProjectId();
  const patient = usePatient();
  const { data: patientData } = usePatientData();

  const { user, accessToken } = useAuth();

  //
  // decision components
  //
  const [score, setScore] = useState<PatientReviewScore | null>(null);
  const [manualNotes, setManualNotes] = useState<string | null>(null);
  const [systemNotes, setSystemNotes] = useState<string | null>(null);
  const [systemReviewScore, setSystemReviewScore] = useState<PatientReviewScore | null>(null);

  const unsaveableReason = score === null ? 'A score is needed to save a review' : undefined;
  const isReviewSavable = unsaveableReason === undefined;

  const [{ loading: reviewSaving, error: reviewSaveError }, setReviewSavingState] = useState<
    LoadableData<PatientReview>
  >(initialLoadableData());
  const [errorMessageShown, setErrorMessageShown] = useState(false);

  //
  // reload the existing review when the patient data changes
  //
  useEffect(() => {
    setScore(patientData?.manualReview?.score ?? null);
    setManualNotes(patientData?.manualReview?.notes ?? null);
    setSystemNotes(patientData?.systemReview?.notes ?? null);
    setSystemReviewScore(patientData?.systemReview?.score ?? null);
  }, [patientData]);

  const undecidedAvailability: DecisionAvailability = {
    actionAvailable: true,
    tooltip: isReviewSavable ? undefined : 'Skip without saving review',
  };

  const rejectAvailability: DecisionAvailability = {
    actionAvailable: isReviewSavable,
    tooltip: isReviewSavable ? undefined : unsaveableReason,
  };

  const acceptAvailability: DecisionAvailability = {
    actionAvailable: isReviewSavable,
    tooltip: isReviewSavable ? undefined : unsaveableReason,
  };

  const applyClientSideReview = useApplyClientSideReview();

  const onDecisionSelected = (status: ReviewStatus['reviewStatusId']) => {
    if (researchProjectId === undefined || patient === undefined) {
      return;
    }

    if (isReviewSavable) {
      const review: PatientReview = {
        patientReviewId: '00000000-0000-0000-0000-000000000000', // Actual id will be assigned by the server
        reviewStatusId: status,
        researchProjectId: researchProjectId,
        deidentity: patient.deidentity,
        reviewedBy: user?.name ?? '',
        reviewedAt: new Date(),
        notes: manualNotes,
        score: score,
      };

      trackLoading(() => CreatePatientReview(review, accessToken), setReviewSavingState)
        .then(() => applyClientSideReview(review))
        .then(() => onDoneReviewing())
        .catch(_ => setErrorMessageShown(true));
    } else {
      onDoneReviewing();
    }
  };

  return (
    <Stack direction='column'>
      <ReviewNotes
        label='System review notes'
        labelAdornment={
          <Stack direction='row' alignItems='center' spacing={1}>
            <CopyButton text={systemNotes ?? ''} size='small' />

            <Tooltip title='Apply system review' placement='top'>
              <IconButton
                size='small'
                onClick={() => {
                  setManualNotes(systemNotes ?? '');
                  setScore(systemReviewScore);
                }}
              >
                <AddTaskOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        }
        reviewNotes={systemNotes}
      />

      <ReviewNotes label='Manual review notes' reviewNotes={manualNotes} setReviewNotes={setManualNotes} />

      <Stack direction='row' spacing={2}>
        <Box sx={{ flex: 1 }}>
          <ScorePicker score={score} onScoreChange={setScore} />
        </Box>
        <Stack direction='row' alignItems='center' justifyContent='center' spacing={2} sx={{ flex: 0 }}>
          <CircularProgress size={24} sx={{ visibility: reviewSaving ? 'visible' : 'hidden' }} />
          <DecisionButtons
            undecidedAvailability={undecidedAvailability}
            rejectAvailability={rejectAvailability}
            acceptAvailability={acceptAvailability}
            onDecisionSelected={onDecisionSelected}
          />
        </Stack>
      </Stack>

      <Snackbar open={errorMessageShown} autoHideDuration={5000} onClose={() => setErrorMessageShown(false)}>
        <Alert severity='error' onClose={() => setErrorMessageShown(false)}>
          Failed to save review: {reviewSaveError}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default ReviewGroup;
